import { IShipperAddress, IStopDetailsData } from './addQuote.interface';
import { DELIVERY_WINDOW_TYPE_OPTIONS, PICKUP_WINDOW_TYPE_OPTIONS, WINDOW_TYPE } from './Steps/addQuoteSteps.const';

// Multi-stop route helpers — key naming/parsing for dynamic middle-stop steps.
// A "stop step" is any step beyond the fixed Shipper/Consignee bookends when
// stopCount > 2 (e.g. 'stop_details_2', 'stop_details_3', ...). These keys are
// intentionally NOT part of the closed AddQuoteKeysType union (see addQuote.interface.ts)
// since they're dynamically generated per stopCount.
const STOP_STEP_KEY_PREFIX = 'stop_details_';

export const stopStepKey = (position: number): string => `${STOP_STEP_KEY_PREFIX}${position}`;

export const isStopStepKey = (key: string | null | undefined): boolean => !!key && key.startsWith(STOP_STEP_KEY_PREFIX);

export const stopPositionFromKey = (key: string): number => parseInt(key.replace(STOP_STEP_KEY_PREFIX, ''), 10);

// ─── Activity hint (Task 11 / P1.5) ─────────────────────────────────────────────────────────
// Optional UI hint for what happens at a middle stop ("What happens at this stop?"). Rides
// IStopDetailsData.activityHint only — buildMultiStopPayload below never reads or sends it (see
// its test assertion). Derived item routing (pickupStopPosition/dropStopPosition per dimension)
// remains the single source of truth for what actually happens; the hint now also hard-filters
// which stops are even offered as routing options (Task 20 — see buildStopOptions below), plus
// which window options / services are offered.
export const ACTIVITY_HINT = {
    PICKUP: 'pickup',
    DELIVERY: 'delivery',
    BOTH: 'both',
} as const;

export type ActivityHintType = (typeof ACTIVITY_HINT)[keyof typeof ACTIVITY_HINT];

// Per-item pick/drop routing (multileg Task 7, hard-filtered by hint per Task 20 client decision)
// — builds the SelectBox option lists for the Dimensions line-item routing selectors. Stop 1 is
// always the shipper (can't be a drop stop), stop N is always the consignee (can't be a pickup
// stop) — enforced positionally below, independent of any hint. A middle stop hinted Pick-up-only
// is excluded from dropOptions entirely (it can never be a delivery point); hinted Deliver-only is
// excluded from pickupOptions entirely. "Both" (or no hint at all — Stop 1/Final never carry one)
// stays eligible for both directions. This replaced a softer, non-blocking "mismatch suffix" — see
// git history — once hinted stops are hard-filtered, a mismatched selection can no longer be made
// in the first place, so there's nothing left to flag.
export const buildStopOptions = (
    t: any,
    args: {
        stopCount: number;
        shipperCity?: string | null;
        shipperState?: string | null;
        shipperStateCode?: string | null;
        shipperCompanyName?: string | null;
        consigneeCity?: string | null;
        consigneeState?: string | null;
        consigneeStateCode?: string | null;
        consigneeCompanyName?: string | null;
        stops: Record<number, any>;
    }
) => {
    // Bug fix (user-reported) — EVERY option in this dropdown (bookends AND middle stops) shows
    // the SAME clean "company name, city, state" format — no "Stop N ·"/"Shipper ·"/"Consignee ·"
    // word prefix anywhere, for full consistency across the whole list. `value` is still the plain
    // position number either way, so what gets saved never changes, only the display label.
    const detail = (companyName?: string | null, city?: string | null, state?: string | null): string => [companyName, [city, state].filter(Boolean).join(', ')].filter(Boolean).join(', ');

    const option = (pos: number) => {
        if (pos === 1) {
            return { label: detail(args.shipperCompanyName, args.shipperCity, args.shipperState || args.shipperStateCode), value: pos };
        }

        if (pos === args.stopCount) {
            return { label: detail(args.consigneeCompanyName, args.consigneeCity, args.consigneeState || args.consigneeStateCode), value: pos };
        }

        const stopAddress = args.stops?.[pos]?.stopAddress;

        return { label: detail(args.stops?.[pos]?.companyName, stopAddress?.city, stopAddress?.state || stopAddress?.stateCode), value: pos };
    };
    const hintOf = (pos: number) => args.stops?.[pos]?.activityHint;
    const allowsPickup = (pos: number) => hintOf(pos) !== ACTIVITY_HINT.DELIVERY;
    const allowsDrop = (pos: number) => hintOf(pos) !== ACTIVITY_HINT.PICKUP;
    const all = Array.from({ length: args.stopCount }, (_, i) => i + 1);
    // Bug fix (user-reported) — a stop not yet given an address (row now shows as soon as ANY
    // location has one, not all) built an option with an empty label, which could end up the
    // selected value with nothing visible in the dropdown. Never surface a blank option.
    const hasLabel = (o: { label: string; value: number }) => o.label.trim().length > 0;

    return {
        pickupOptions: all
            .filter((p) => p < args.stopCount && allowsPickup(p))
            .map(option)
            .filter(hasLabel),
        dropOptions: all
            .filter((p) => p > 1 && allowsDrop(p))
            .map(option)
            .filter(hasLabel),
    };
};

// Window-type options offered per activity hint (Task 11 §2). Pickup gets
// PICKUP_WINDOW_TYPE_OPTIONS (Business Hours / Ready by / By appointment), delivery gets
// DELIVERY_WINDOW_TYPE_OPTIONS (Business Hours / Deliver by / By appointment), and "both" (the
// default) is the union — Business Hours / Ready by / Deliver by / By appointment — built from
// named lookups (not array indices) so it stays correct if either source list is reordered.
export const getStopWindowOptions = (hint: ActivityHintType | null | undefined): { label: string; value: string }[] => {
    if (hint === ACTIVITY_HINT.PICKUP) return PICKUP_WINDOW_TYPE_OPTIONS;
    if (hint === ACTIVITY_HINT.DELIVERY) return DELIVERY_WINDOW_TYPE_OPTIONS;

    const openOption = PICKUP_WINDOW_TYPE_OPTIONS.find((o) => o.value === WINDOW_TYPE.OPEN)!;
    const readyOption = PICKUP_WINDOW_TYPE_OPTIONS.find((o) => o.value === WINDOW_TYPE.READY_TIME)!;
    const deliverOption = DELIVERY_WINDOW_TYPE_OPTIONS.find((o) => o.value === WINDOW_TYPE.DELIVERY_TIME)!;
    const appointmentOption = PICKUP_WINDOW_TYPE_OPTIONS.find((o) => o.value === WINDOW_TYPE.APPOINTMENT)!;

    return [openOption, readyOption, deliverOption, appointmentOption];
};

// Whether a previously-selected windowType is still offered under a (possibly new) hint — used
// to decide whether switching the activity hint should reset windowType/times or leave them
// alone. Task 11 §2: "switching the hint keeps entered values where compatible; if a selected
// windowType is invalid for the new hint, reset windowType (and its times) ... keep the date."
// A null/unset windowType is always "valid" (nothing to reset).
export const isWindowTypeValidForHint = (windowType: string | null | undefined, hint: ActivityHintType | null | undefined): boolean => {
    if (!windowType) return true;

    return getStopWindowOptions(hint).some((o) => o.value === windowType);
};

// Additional-services list offered per activity hint (Task 11 §3) — mirrors ShipperDetails'/
// ConsigneeDetails' serviceTypes.PICKUP / serviceTypes.DELIVERY split exactly; "both" (the
// default) shows the union, deduped by id, since a stop acting as both pickup and delivery can
// offer either a pickup-only or delivery-only accessorial.
export const getServiceTypesForHint = (serviceTypes: { PICKUP?: any[]; DELIVERY?: any[] } | null | undefined, hint: ActivityHintType | null | undefined): any[] => {
    const pickup = serviceTypes?.PICKUP || [];
    const delivery = serviceTypes?.DELIVERY || [];

    if (hint === ACTIVITY_HINT.PICKUP) return pickup;
    if (hint === ACTIVITY_HINT.DELIVERY) return delivery;

    const seen = new Set<number>();

    return [...pickup, ...delivery].filter((s) => {
        if (seen.has(s.id)) return false;
        seen.add(s.id);

        return true;
    });
};

// Task 20 (client decision) — the previous soft routing-mismatch detector (detectStopHintMismatches)
// is removed: now that buildStopOptions hard-filters by hint, a mismatched selection can no
// longer be made through the UI in the first place, so there was nothing left for it to flag.
// clearInvalidStopRouting (below) handles the one remaining case: an item that was routed through
// a stop BEFORE the rep changed that stop's hint to something incompatible.

// Auto-clears a dimension item's pickupStopPosition/dropStopPosition when the stop it's routed
// through no longer allows that direction (its hint changed since the item was routed) — e.g. an
// item "Delivers to: Stop 2" when Stop 2 gets re-hinted "Pick up" only. Pure function: returns the
// positions to clear per item index; the caller (AddDimensions) applies them via setValue.
export const clearInvalidStopRouting = (args: {
    stops: Record<number, any>;
    items: { pickupStopPosition?: number | null; dropStopPosition?: number | null }[];
}): { index: number; field: 'pickupStopPosition' | 'dropStopPosition' }[] => {
    const hintOf = (pos: number) => args.stops?.[pos]?.activityHint;
    const allowsPickup = (pos: number) => hintOf(pos) !== ACTIVITY_HINT.DELIVERY;
    const allowsDrop = (pos: number) => hintOf(pos) !== ACTIVITY_HINT.PICKUP;
    const toClear: { index: number; field: 'pickupStopPosition' | 'dropStopPosition' }[] = [];

    args.items.forEach((item, index) => {
        if (item.pickupStopPosition && !allowsPickup(item.pickupStopPosition)) {
            toClear.push({ index, field: 'pickupStopPosition' });
        }

        if (item.dropStopPosition && !allowsDrop(item.dropStopPosition)) {
            toClear.push({ index, field: 'dropStopPosition' });
        }
    });

    return toClear;
};

// Same routing rules as BE Task 2's item-routing validator (drop-after-pick, stop 1 boards,
// stop N receives, every middle stop must have activity) — kept in sync intentionally so FE
// can block submission before the round trip to the BE validator.
export const validateItemRouting = (args: {
    stopCount: number;
    items: { pickupStopPosition?: number | null; dropStopPosition?: number | null }[];
}): { valid: boolean; errors: { messageKey: string; n?: number }[] } => {
    const errors: { messageKey: string; n?: number }[] = [];
    // Classic (unrouted) items default to pickup=stop1/drop=stopN, matching the classic
    // 2-stop behavior where every item boards at the shipper and is delivered at the consignee.
    const items = args.items.map((i) => ({
        on: Number(i.pickupStopPosition ?? 1),
        off: Number(i.dropStopPosition ?? args.stopCount),
    }));

    items.forEach(({ on, off }) => {
        if (off <= on) errors.push({ messageKey: 'MyQuote.Item_Drop_After_Pick' });
    });

    if (!items.some((i) => i.on === 1)) errors.push({ messageKey: 'MyQuote.First_Stop_Must_Board' });
    if (!items.some((i) => i.off === args.stopCount)) errors.push({ messageKey: 'MyQuote.Last_Stop_Must_Receive' });

    for (let pos = 2; pos < args.stopCount; pos++) {
        if (!items.some((i) => i.on === pos || i.off === pos)) errors.push({ messageKey: 'MyQuote.Stop_No_Activity', n: pos });
    }

    return { valid: errors.length === 0, errors };
};

// Multi-stop submit (Task 8) — maps an address-shaped object (shipperAddress/consigneeAddress/
// stopAddress) onto the flat address fields the BE's POST /quotes/multi-stop Joi schema requires
// on EVERY stop (address1/state/country/postal are NOT-NULL on the Quote model's shipper*/
// consignee* columns, and any stop can anchor the master quote — see BE
// createMultiStopQuote.controller.ts / multiStopStopSchema).
export const addressFields = (addr: any) => ({
    fullAddress: addr?.fullAddress ?? null,
    address1: addr?.address1 ?? null,
    // Bug fix (user-reported) — the address picker splits the street number OUT of address1
    // (ShipperConsigneeAutocomplete.tsx/AutoCompleteGoogle.tsx: `address1 = address1.replace(
    // streetNumber, '')`), so it must ride as its own field here or it's silently dropped before
    // the request is even built — reopening Edit later showed an incomplete address.
    streetNumber: addr?.streetNumber ?? null,
    city: addr?.city ?? null,
    state: addr?.state ?? null,
    stateCode: addr?.stateCode ?? null,
    country: addr?.country ?? null,
    countryCode: addr?.countryCode ?? null,
    postal: addr?.postal ?? null,
    latitude: addr?.latitude ?? null,
    longitude: addr?.longitude ?? null,
});

// Pure payload builder for the multi-stop submit (Task 8) — takes the AddQuote slice's per-step
// data (shipper/consignee/middle stops/shipment/dimensions) and produces the exact
// { quote, stops, dimensions } contract POST /quotes/multi-stop expects. Kept pure/side-effect-free
// so it's unit-testable without Redux — the caller (useSaveNextStep) is responsible for reading the
// slice and merging in the freshest totalWeight/linearFootage (which live on the Dimensions form's
// `data`, not the ShipmentDetails slice — see useSaveNextStep.tsx for that merge).
export const buildMultiStopPayload = (args: {
    stopCount: number;
    divisionId: number | null;
    customerBillingAccountId: number | null;
    serviceType: string | null;
    urgency: string | null;
    serviceLevel?: string | null;
    shipperDetails: any;
    consigneeDetails: any;
    stops: Record<number, any>;
    shipmentDetails: any;
    dimensions: any[];
    weightMeasurement?: string;
    // Dimensions step's "Other Additional Services" block (inbond / dangerous goods / excess
    // valuation source fields) — mirrors createApiPayload's `effectiveDimensionsDetails?.additionalServices`
    // (see useSaveNextStep.tsx). Passed separately from `shipmentDetails` because it lives on the
    // Dimensions slice, not the Shipment Details slice.
    additionalServices?: any;
}) => {
    const { stopCount, shipperDetails, consigneeDetails } = args;
    const stops: any[] = [];

    stops.push({
        position: 1,
        companyName: shipperDetails?.shipperCompanyName ?? null,
        // User decision — every stop persists its saved address-book link (quote_stops.locationId),
        // not just the bookends' quote-level shipperId/consigneeId — same value, now dual-written.
        locationId: shipperDetails?.shipperId ?? null,
        ...addressFields(shipperDetails?.shipperAddress),
        dateType: shipperDetails?.pickupDate ? (shipperDetails?.pickupEndDate ? 'flexible' : 'fixed') : 'open',
        date: shipperDetails?.pickupDate || null,
        endDate: shipperDetails?.pickupEndDate || null,
        windowType: shipperDetails?.pickupWindowType || null,
        startTime: shipperDetails?.pickupStartTime || null,
        endTime: shipperDetails?.pickupEndTime || null,
        serviceIds: shipperDetails?.serviceId ?? [],
    });

    for (let pos = 2; pos <= stopCount - 1; pos++) {
        const stop = args.stops?.[pos];

        stops.push({
            position: pos,
            companyName: stop?.companyName ?? null,
            // User decision — a middle stop's saved address-book link (was captured client-side
            // via StopDetails/index.tsx's locationId but never forwarded before).
            locationId: stop?.locationId ?? null,
            ...addressFields(stop?.stopAddress),
            dateType: stop?.dateType ?? 'open',
            date: stop?.date || null,
            endDate: stop?.endDate || null,
            windowType: stop?.windowType || null,
            startTime: stop?.startTime || null,
            endTime: stop?.endTime || null,
            serviceIds: stop?.serviceId ?? [],
            // Task 22 — Limited Access Delivery persists per-stop (quote_selected_services keyed
            // by stopId, quoteId left null), unlike Stop 1/Final's, which rides the master
            // `services` block below via consigneeDetails.limitedAccessDelivery*. Wire field names
            // drop the "Delivery" infix to match the quote_selected_services column names directly.
            limitedAccessDelivery: stop?.limitedAccessDelivery ?? false,
            limitedAccessType: stop?.limitedAccessDeliveryType || null,
            limitedAccessOther: stop?.limitedAccessDeliveryOther || null,
        });
    }

    stops.push({
        position: stopCount,
        companyName: consigneeDetails?.consigneeCompanyName ?? null,
        locationId: consigneeDetails?.consigneeId ?? null,
        ...addressFields(consigneeDetails?.consigneeAddress),
        dateType: consigneeDetails?.deliveryDate ? (consigneeDetails?.deliveryEndDate ? 'flexible' : 'fixed') : 'open',
        date: consigneeDetails?.deliveryDate || null,
        endDate: consigneeDetails?.deliveryEndDate || null,
        windowType: consigneeDetails?.deliveryWindowType || null,
        startTime: consigneeDetails?.deliveryStartTime || null,
        endTime: consigneeDetails?.deliveryEndTime || null,
        serviceIds: consigneeDetails?.serviceId ?? [],
    });

    // Additional services (Task 8b) — mirrors createApiPayload's `services` object in
    // useSaveNextStep.tsx EXACTLY: limited access comes from ConsigneeDetails (not Dimensions —
    // see IConsigneeDetailsData.limitedAccessDelivery*), inbond/dangerousGoods come from the
    // Dimensions step's additionalServices block, and serviceId merges shipper + consignee +
    // dimensions selections the same way classic does (so the special service ids referenced by
    // the limitedAccess/inbond/dangerousGoods flags are actually present in the id list the BE
    // walks to attach the extra fields).
    const additionalServices = args.additionalServices;
    const pickupServiceIds: number[] = (shipperDetails?.serviceId || []).map(Number).filter(Boolean);
    const deliveryServiceIds: number[] = (consigneeDetails?.serviceId || []).map(Number).filter(Boolean);
    const otherServiceIds: number[] = (additionalServices?.serviceId || []).map(Number).filter(Boolean);
    const combinedServiceIds = [...pickupServiceIds, ...deliveryServiceIds, ...otherServiceIds];
    const allServiceIds = combinedServiceIds.filter((id, index) => combinedServiceIds.indexOf(id) === index);

    const services = {
        serviceId: allServiceIds.join(','),
        // Limited access delivery is collected in ConsigneeDetails step (not Dimensions)
        limitedAccess: consigneeDetails?.limitedAccessDelivery ?? false,
        ...(consigneeDetails?.limitedAccessDelivery && {
            limitedAccessType: consigneeDetails?.limitedAccessDeliveryType,
            limitedAccessOther: consigneeDetails?.limitedAccessDeliveryOther,
        }),
        inbond: additionalServices?.inbond,
        ...(additionalServices?.inbond && {
            inbondType: additionalServices?.inbondType,
            inbondName: additionalServices?.inbondName,
            inbondAddress: additionalServices?.inbondAddress,
            inbondContactMethod: additionalServices?.inbondContactMethod,
            inbondContactValue: additionalServices?.inbondContactValue,
        }),
        dangerousGoods: additionalServices?.dangerousGoods,
        ...(additionalServices?.dangerousGoods && {
            dangerGoodsType: additionalServices?.dangerGoodsType,
            unitedNationsNumber: additionalServices?.unitedNationsNumber,
            packagingGroup: additionalServices?.packagingGroup,
            goodsClass: additionalServices?.goodsClass,
            description: additionalServices?.description,
            emergencyContactName: additionalServices?.emergencyContactName,
            emergencyContactNumber: additionalServices?.emergencyContactNumber,
            emergencyContactExt: additionalServices?.emergencyContactExt,
            emergencyEapnbr: additionalServices?.emergencyEapnbr,
        }),
    };

    return {
        quote: {
            divisionId: args.divisionId,
            customerBillingAccountId: args.customerBillingAccountId,
            classification: args.serviceType ?? 'ltl',
            urgency: args.urgency,
            serviceLevel: args.serviceLevel ?? null,
            // Bug fix (user-reported) — when the shipper/consignee is picked from a SAVED
            // address-book record (not freshly typed/picked from Google Places), persist that
            // link — mirrors createApiPayload's `address.shipperId`/`consigneeId` (classic create)
            // and the Edit modal's own createPayloadFromAddressForm, neither of which this multi-
            // stop payload builder was forwarding before.
            shipperId: shipperDetails?.shipperId ?? null,
            consigneeId: consigneeDetails?.consigneeId ?? null,
            totalWeight: args.shipmentDetails?.totalWeight ?? null,
            linearFootage: args.shipmentDetails?.linearFootage ?? null,
            primaryEquipmentType: args.shipmentDetails?.primaryEquipmentType ?? null,
            primaryEquipmentTypeFullName: args.shipmentDetails?.primaryEquipmentTypeFullName ?? null,
            secondaryEquipmentTypes: args.shipmentDetails?.secondaryEquipmentTypes ?? null,
            secondaryEquipmentTypeFullNames: args.shipmentDetails?.secondaryEquipmentTypeFullNames ?? null,
            // Insurance (Task 8b — closes the gap found in Task 8's review): forwarded from the
            // ShipmentDetails step slice, mirroring createApiPayload's `address.insurance*` fields
            // in useSaveNextStep.tsx. insuranceCommercialValue/insuranceCommercialValueUSD are
            // expected to ALREADY be CAD/USD-converted by the caller (useSaveNextStep does the
            // getPricingRate()+convertCurrency() work — kept out of this function so it stays a
            // pure, synchronously-testable builder, same layering classic uses).
            isLoadInsuranceEnabled: args.shipmentDetails?.isLoadInsuranceEnabled ?? false,
            insuranceCommodity: args.shipmentDetails?.insuranceCommodity ?? null,
            insuranceCommercialValue: args.shipmentDetails?.insuranceCommercialValue ?? null,
            insuranceCommercialValueUSD: args.shipmentDetails?.insuranceCommercialValueUSD ?? null,
            insuranceCurrencyType: args.shipmentDetails?.insuranceCurrencyType ?? null,
        },
        stops,
        dimensions: args.dimensions.map((d) => ({
            handlingUnit: d.handlingUnit,
            handlingUnitNo: d.handlingUnitNo,
            weight: d.weight,
            itemLength: d.itemLength,
            width: d.width,
            height: d.height,
            totalWeight: d.totalWeight,
            isStack: d.isStack,
            commodity: d.commodity ?? null,
            freightClass: d.freightClass ?? 0,
            itemClassForDayross: d.itemClassForDayross ?? 0,
            weightMeasurement: args.weightMeasurement ?? 'LBS',
            pickupStopPosition: d.pickupStopPosition ?? 1,
            dropStopPosition: d.dropStopPosition ?? stopCount,
        })),
        services,
    };
};

// ─── Task 35/40 — Edit Quote's per-middle-stop editing (address/services/limited-access/hint) ──
// Existing-stop analogue of IStopDetailsData, without date/window/locationId/locationType — all
// locked or unavailable post-creation (QuoteStop never persists those columns). `id` is required
// since this is an update-by-id, not a position-keyed create.
//
// Task 40 (client decision, revised) — activityHint IS included here and IS editable (same
// Pickup/Deliver/Both chips as StopDetails.tsx) AND is now persisted: QuoteStop has an
// activityHint column (quote_stops.activityHint), so buildEditStopPayload below sends it and
// toEditStopFormData seeds it back from the saved value on reopen. It still drives this editing
// session's service-section visibility, address-list search direction, and Load Details' per-item
// routing dropdown filtering (buildStopOptions/clearInvalidStopRouting). Defaults to BOTH (least
// restrictive) only when the stop has no saved hint yet (e.g. created before this column existed).
// Note: the create flow (Add Quote) still does not send this — its own IStopDetailsData.activityHint
// remains session-local, out of scope for this change.
export interface IEditStopFormData {
    id: number;
    position: number;
    companyName: string;
    stopAddress: IShipperAddress | null;
    // User decision — saved address-book link (Shippers table); null when the address was
    // freshly typed/picked instead of selected from a saved record.
    locationId: number | null;
    serviceId: number[];
    limitedAccessDelivery: boolean;
    limitedAccessDeliveryType: string;
    limitedAccessDeliveryOther: string;
    activityHint: ActivityHintType;
}

// Maps a raw QuoteStop row (as returned by getQuoteById/updateQuote's additive `stops` field) to
// this feature's editable form state.
export const toEditStopFormData = (stop: any): IEditStopFormData => ({
    id: stop.id,
    position: stop.position,
    companyName: stop.companyName || '',
    locationId: stop.locationId ?? null,
    stopAddress: stop.fullAddress
        ? {
              fullAddress: stop.fullAddress,
              address1: stop.address1 || '',
              // Bug fix (user-reported) — round-trip the street number back from the saved stop
              // so reopening Edit doesn't show an incomplete address (see addressFields above).
              streetNumber: stop.streetNumber || '',
              city: stop.city || '',
              country: stop.country || '',
              countryCode: stop.countryCode || '',
              latitude: stop.latitude || '',
              longitude: stop.longitude || '',
              postal: stop.postal || '',
              state: stop.state || '',
              stateCode: stop.stateCode || '',
          }
        : null,
    serviceId: Array.isArray(stop.serviceIds) ? stop.serviceIds.map(Number) : [],
    limitedAccessDelivery: !!stop.limitedAccessDelivery,
    limitedAccessDeliveryType: stop.limitedAccessType || '',
    limitedAccessDeliveryOther: stop.limitedAccessOther || '',
    activityHint: (stop.activityHint as ActivityHintType) || ACTIVITY_HINT.BOTH,
});

// Duplicate Quote (multi-stop) — converts a fetched quote's additive `stops` block (getQuoteById)
// plus its routed `dimensions` into the Add Quote slice's multi-stop state, so duplicating a
// 5-stop quote lands the customer in the same 5-stop wizard instead of the 2-stop one.
//
// Why positions are renumbered: the CP only receives CUSTOMER-VISIBLE stops (BE
// filterCustomerVisibleStops drops WAL-added middle stops), so the raw `position` column can have
// gaps (e.g. 1, 2, 4, 5). The wizard's stopCount/stops/dimension routing all assume a contiguous
// 1..N sequence, so every stop is renumbered by its sort order and the same original→new map is
// applied to each dimension's pickupStopPosition/dropStopPosition. A routing that pointed at a
// dropped (non-visible) stop becomes null, which the Dimensions step already defaults to the
// shipper/consignee bookends. Returns null for classic quotes (fewer than 3 visible stops) so
// callers can fall through to the untouched 2-stop duplicate path.
export const buildDuplicateMultiStopState = (
    rawStops: any[] | null | undefined,
    dimensions: any[] | null | undefined
): { stopCount: number; stops: Record<number, IStopDetailsData>; dimensions: any[] } | null => {
    const sorted = [...(rawStops ?? [])].filter((s) => s && typeof s.position === 'number').sort((a, b) => a.position - b.position);

    if (sorted.length <= 2) return null;

    const stopCount = sorted.length;
    const positionMap: Record<number, number> = {};
    sorted.forEach((stop, index) => {
        positionMap[stop.position] = index + 1;
    });

    const stops: Record<number, IStopDetailsData> = {};
    // Middle stops only — Stop 1/Final are carried by shipperDetails/consigneeDetails, which the
    // caller maps from the quote's own shipper*/consignee* columns (same as the classic path).
    sorted.slice(1, -1).forEach((stop) => {
        // id/position belong to the source quote's rows — a duplicate must not carry them.
        const editShape: Partial<IEditStopFormData> = { ...toEditStopFormData(stop) };
        delete editShape.id;
        delete editShape.position;

        stops[positionMap[stop.position]] = {
            ...(editShape as Omit<IEditStopFormData, 'id' | 'position'>),
            // Dates ride through raw (same as the classic path's pickupDate/deliveryDate) — the
            // StopDetails step normalizes with moment(). dateType falls back to 'open' (its own
            // default) for stops saved without one.
            dateType: stop.dateType || 'open',
            date: stop.date ?? null,
            endDate: stop.endDate ?? null,
            windowType: stop.windowType ?? null,
            startTime: stop.startTime ?? null,
            endTime: stop.endTime ?? null,
            // locationType isn't persisted on quote_stops; StopDetails derives the search list from
            // activityHint when it's null, so leaving it unset is the correct "unknown" value.
            locationType: null,
        };
    });

    const remap = (position: number | null | undefined): number | null => (position != null && positionMap[position] != null ? positionMap[position] : null);

    const remappedDimensions = (dimensions ?? []).map((dimension: any) => ({
        ...dimension,
        pickupStopPosition: remap(dimension?.pickupStopPosition),
        dropStopPosition: remap(dimension?.dropStopPosition),
    }));

    return { stopCount, stops, dimensions: remappedDimensions };
};

// Bug fix (user-reported) — clicking a Stop Activity pill in EditStopDetails.tsx must NOT clear
// serviceId (that would discard DB-saved/session-entered values just from browsing hints, before
// ever saving). The actual cleanup — dropping a Pickup-only or Delivery-only service once the
// FINAL hint at save time no longer calls for it — happens exactly once, here, when the payload is
// built. Services outside both lists (e.g. in-bond, load_insurance) are never touched.
export const pruneServiceIdsForHint = (serviceId: number[], hint: ActivityHintType, pickupServiceIds: number[], deliveryServiceIds: number[]): number[] => {
    const pickupIds = new Set(pickupServiceIds);
    const deliveryIds = new Set(deliveryServiceIds);
    const keepPickup = hint === ACTIVITY_HINT.PICKUP || hint === ACTIVITY_HINT.BOTH;
    const keepDelivery = hint === ACTIVITY_HINT.DELIVERY || hint === ACTIVITY_HINT.BOTH;

    return (serviceId ?? []).filter((id) => {
        if (pickupIds.has(id)) return keepPickup;
        if (deliveryIds.has(id)) return keepDelivery;

        return true;
    });
};

// Maps one edited stop back to the wire shape updateQuote.controller.ts's `stops[]` array
// expects — field names mirror buildMultiStopPayload's middle-stop convention exactly
// (serviceIds, limitedAccessType/Other dropping the "Delivery" infix) so the two stay consistent.
// `serviceCategoryIds` is optional so existing callers/tests that don't pass it keep the previous
// pass-through behavior; QuoteDetailModalNew.tsx (the real save path) always supplies it.
export const buildEditStopPayload = (stop: IEditStopFormData, serviceCategoryIds?: { pickupServiceIds: number[]; deliveryServiceIds: number[] }) => {
    const serviceIds = serviceCategoryIds
        ? pruneServiceIdsForHint(stop.serviceId ?? [], stop.activityHint, serviceCategoryIds.pickupServiceIds, serviceCategoryIds.deliveryServiceIds)
        : stop.serviceId ?? [];
    const keepsDelivery = stop.activityHint === ACTIVITY_HINT.DELIVERY || stop.activityHint === ACTIVITY_HINT.BOTH;
    const limitedAccessDelivery = serviceCategoryIds && !keepsDelivery ? false : stop.limitedAccessDelivery ?? false;

    return {
        id: stop.id,
        companyName: stop.companyName || null,
        locationId: stop.locationId ?? null,
        ...addressFields(stop.stopAddress),
        serviceIds,
        limitedAccessDelivery,
        limitedAccessType: limitedAccessDelivery ? stop.limitedAccessDeliveryType || null : null,
        limitedAccessOther: limitedAccessDelivery ? stop.limitedAccessDeliveryOther || null : null,
        activityHint: stop.activityHint || null,
    };
};
